import { useEffect, useMemo, useState } from 'react';
import { X, Save, Trash2, Plus, MapPin, Trophy } from 'lucide-react';
import {
  SLOT_COUNT,
  type SlotMeta,
  deleteSlot,
  isSlotOccupied,
  readActiveSlotId,
  readSlotMetas,
  writeActiveSlotId,
} from '@/lib/saveSlots';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_DIALOGS_HIGH, Z_MODALS } from '@/lib/zIndex';
import { formatEuro } from '@/lib/format';
import { MODE_LABELS } from '@/lib/saveGame';
import type { GameMode } from '@/state/types';

interface SaveBrowserProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Phase 7.1.1 — Save-Slot-Browser. Up to 3 parallel save slots.
 * Switching slots reloads the page (necessary so the persist middleware
 * picks up the new key on store init).
 */
export function SaveBrowser({ open, onClose }: SaveBrowserProps) {
  const [metas, setMetas] = useState<SlotMeta[]>(readSlotMetas());
  const [activeId, setActiveId] = useState<number>(readActiveSlotId());
  const [confirmDelete, setConfirmDelete] = useState<SlotMeta | null>(null);
  const [creatingForSlot, setCreatingForSlot] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setMetas(readSlotMetas());
    setActiveId(readActiveSlotId());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const refresh = () => setMetas(readSlotMetas());

  const handleSwitch = (slotId: number) => {
    if (slotId === activeId) {
      onClose();
      return;
    }
    writeActiveSlotId(slotId);
    // Force a fresh init of the persisted store
    window.location.reload();
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    deleteSlot(confirmDelete.id);
    if (confirmDelete.id === activeId) {
      // Active slot wiped — reset to slot 1 and reload
      writeActiveSlotId(1);
      window.location.reload();
      return;
    }
    setConfirmDelete(null);
    refresh();
  };

  return (
    <>
      {open && (
        <ModalRoot
          onClose={onClose}
          zIndex={Z_DIALOGS_HIGH}
          contentClass="w-full max-w-3xl rounded-lg border border-border-strong bg-bg-secondary"
        >
          <header className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-dispatch/15 text-accent-dispatch">
                <Save className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-accent-dispatch">
                  Spielstände
                </p>
                <h3 id="save-browser-title" className="font-display text-lg font-semibold">
                  Bis zu {SLOT_COUNT} parallele Spielstände
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="text-fg-muted transition hover:text-fg-primary"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </header>

          <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-3">
            {metas.map((m) => (
              <SlotCard
                key={`slot-${m.id}`}
                meta={m}
                isActive={m.id === activeId}
                onSwitch={() => handleSwitch(m.id)}
                onDelete={() => setConfirmDelete(m)}
                onCreate={() => setCreatingForSlot(m.id)}
              />
            ))}
          </div>

          <footer className="border-t border-border-subtle px-6 py-3 text-xs text-fg-muted">
            Wechseln auf einen anderen Slot lädt die Seite neu, damit der Spielstand
            vollständig geladen werden kann.
          </footer>
        </ModalRoot>
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        title={`Spielstand 'Slot ${confirmDelete?.id}' löschen?`}
        description={`${confirmDelete ? slotSummary(confirmDelete) : ''} — diese Aktion ist endgültig.`}
        confirmLabel="Löschen"
        tone="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <NewSlotDialog
        open={creatingForSlot !== null}
        slotId={creatingForSlot ?? 1}
        onClose={() => setCreatingForSlot(null)}
        onConfirm={(slotId, _mode) => {
          if (typeof window !== 'undefined') {
            try {
              sessionStorage.setItem('rescueop_pending_mode', _mode);
            } catch {
              // ignore
            }
          }
          writeActiveSlotId(slotId);
          window.location.reload();
        }}
      />
    </>
  );
}

function slotSummary(m: SlotMeta): string {
  if (!isSlotOccupied(m)) return 'Leerer Slot';
  return `${m.cityName} · ${MODE_LABELS[m.mode]} · Level ${m.level} · ${formatEuro(m.credits)}`;
}

function SlotCard({
  meta,
  isActive,
  onSwitch,
  onDelete,
  onCreate,
}: {
  meta: SlotMeta;
  isActive: boolean;
  onSwitch: () => void;
  onDelete: () => void;
  onCreate: () => void;
}) {
  const occupied = isSlotOccupied(meta);
  return (
    <div
      className={[
        'flex h-full flex-col rounded-lg border p-4 transition',
        isActive
          ? 'border-accent-dispatch bg-accent-dispatch/10'
          : 'border-border-subtle bg-bg-elevated/30',
      ].join(' ')}
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-fg-muted">Slot {meta.id}</p>
        {isActive && (
          <span className="rounded-full bg-accent-dispatch px-2 py-0.5 text-[10px] font-bold text-white">
            Aktiv
          </span>
        )}
      </div>

      {/* Phase 11.2.8 (N11) — Slot has data XOR is empty. The Aktiv-Badge
          implies data; the "Neuer Spielstand" button only renders for truly
          empty (unoccupied) slots. */}
      {occupied ? (
        <>
          <p className="font-display text-base font-bold leading-tight">
            <MapPin className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden />
            {meta.cityName}
          </p>
          <p className="mt-0.5 text-xs text-fg-muted">{MODE_LABELS[meta.mode]}</p>

          <dl className="mt-3 space-y-1 text-xs">
            <div className="flex justify-between">
              <dt className="text-fg-muted">Level</dt>
              <dd className="tabular-nums">{meta.level}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-fg-muted">Geld</dt>
              <dd className="tabular-nums">{formatEuro(meta.credits)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-fg-muted">Zuletzt</dt>
              <dd className="tabular-nums">{formatLastPlayed(meta.lastPlayed)}</dd>
            </div>
          </dl>

          <div className="mt-auto flex items-center gap-2 pt-4">
            <button
              type="button"
              onClick={onSwitch}
              className="flex-1 rounded-md bg-accent-dispatch px-3 py-2 text-sm font-semibold text-white transition hover:bg-accent-dispatch-hover"
            >
              {isActive ? 'Im Spiel' : 'Fortsetzen'}
            </button>
            <button
              type="button"
              onClick={onDelete}
              aria-label="Löschen"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle text-fg-muted transition hover:border-accent-emergency hover:text-accent-emergency"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={onCreate}
          className="mt-auto flex flex-1 flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-strong p-6 text-fg-muted transition hover:border-accent-dispatch hover:text-accent-dispatch"
        >
          <Plus className="h-6 w-6" aria-hidden />
          <span className="text-xs font-semibold">Neuer Spielstand</span>
        </button>
      )}
    </div>
  );
}

function formatLastPlayed(ms: number): string {
  if (ms === 0) return '—';
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function NewSlotDialog({
  open,
  slotId,
  onClose,
  onConfirm,
}: {
  open: boolean;
  slotId: number;
  onClose: () => void;
  onConfirm: (slotId: number, mode: GameMode) => void;
}) {
  const modes = useMemo(
    (): { id: GameMode; label: string; desc: string }[] => [
      { id: 'normal', label: 'Klassik', desc: 'Standard-Schwierigkeit, 3 Mio € Startkapital.' },
      {
        id: 'realistic',
        label: 'Realistisch',
        desc: 'Höhere Strafen, Pause kostet Punkte, 2 Mio € Start.',
      },
      { id: 'sandbox', label: 'Sandbox', desc: 'Unbegrenztes Geld, frei einstellbar.' },
    ],
    [],
  );

  if (!open) return null;
  return (
    <ModalRoot
      onClose={onClose}
      zIndex={Z_MODALS}
      contentClass="w-full max-w-md rounded-lg border border-border-strong bg-bg-secondary p-5"
    >
      <div className="mb-4 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-400" aria-hidden />
        <h3 className="font-display text-base font-semibold">
          Neuer Spielstand in Slot {slotId}
        </h3>
      </div>
      <p className="mb-3 text-xs text-fg-muted">Modus wählen:</p>
      <ul className="space-y-2">
        {modes.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => onConfirm(slotId, m.id)}
              className="flex w-full flex-col items-start gap-1 rounded-md border border-border-subtle bg-bg-elevated/40 p-3 text-left transition hover:border-accent-dispatch hover:bg-bg-elevated"
            >
              <span className="font-display text-sm font-bold">{m.label}</span>
              <span className="text-xs text-fg-muted">{m.desc}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border-strong bg-bg-elevated px-4 py-2 text-sm font-semibold transition hover:bg-bg-elevated/70"
        >
          Abbrechen
        </button>
      </div>
    </ModalRoot>
  );
}
